// NOTE: このファイルはまだちゃんとレビューされていません。コードの書き方を真似しないでください。
import type { FC } from "react"
import { useCallback, useMemo, useRef, useState } from "react"

import MentionRow, { type MentionOverride } from "./MentionRow"
import {
	type DueDateSelectableValue,
	type PriorityFilterValue,
	type PrioritySelectableValue,
	type DueDateFilterValue,
	type RcFilterValue,
	type SearchFilterValue,
	type SenderFilterValue,
	type SessionFilter,
	type WorktreeSlot,
	DUE_DATE_FILTER_OPTIONS,
	PRIORITY_FILTER_OPTIONS,
	RC_FILTER_OPTIONS,
	WORKTREE_SLOTS,
	buildCcCommand,
	copyTextToClipboard,
	formatSyncRelativeTime,
	loadWorktreeSlot,
	normalizeComment,
	saveWorktreeSlot,
	type SlackAppSetting,
} from "./utils"

import { FragmentType, getFragmentData } from "@/souhl_admin/gql/__generated__"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

import styles from "../TaigaPage.module.scss"
import { useIsMobile } from "../shared/CreateSessionModal"
import {
	SessionSelectionContext,
	type SelectedCcSession,
} from "../shared/SessionSelectionContext"

import type { NodeTreeFocusRequest } from "./MentionNodeTree"
import type { SharedNode } from "../shared/NodeRow"

export const SlackMentionsSectionMentionFragment = graphql(/* GraphQL */ `
	fragment SlackMentionsSectionMention on TaigaSlackMention {
		id
		isChildNodesCollapsed
		childNodesCount
		...MentionRow
	}
`)

export const SlackMentionsSectionSyncStateFragment = graphql(/* GraphQL */ `
	fragment SlackMentionsSectionSyncState on TaigaSlackMentionSyncState {
		lastSyncTime
	}
`)

type SlackMentionsSectionProps = {
	mentions: FragmentType<typeof SlackMentionsSectionMentionFragment>[]
	// polling で取得した embedded session の最新 status を node id 単位で渡す。
	polledSessionByNodeId: Record<
		string,
		{
			sessionStatus: string
			isRemoteControlEnabled: boolean
			memo: string | null
		}
	>
	syncState:
		| FragmentType<typeof SlackMentionsSectionSyncStateFragment>
		| null
		| undefined
	loading: boolean
	error?: Error
	syncStateLoading: boolean
	isSyncing: boolean
	isReadOnly: boolean
	focusedMentionId: string | null
	editingMentionId: string | null
	editingComment: string
	overrides: Record<string, MentionOverride>
	priorityFilter: PrioritySelectableValue[]
	dueDateFilter: DueDateSelectableValue[]
	senderFilter: SenderFilterValue
	sessionFilter: SessionFilter
	// メモ (mention のメモ / node のセッションメモ) の部分一致検索キーワード
	searchKeyword: SearchFilterValue
	senderOptions: string[]
	priorityFilterCounts: Record<string, number>
	dueDateFilterCounts: Record<string, number>
	// 送信者名ごとの件数 (送信者以外のフィルタ適用後)
	senderFilterCounts: Record<string, number>
	sessionFilterCounts: {
		busy: number
		idle: number
		all: number
		rc: number
		nonRc: number
	}
	onTogglePriorityFilter: (value: PriorityFilterValue) => void
	onToggleDueDateFilter: (value: DueDateFilterValue) => void
	// 送信者 1 人分の選択を切り替える (null = 全選択 の状態から押したときは、その 1 人を外す)
	onToggleSenderFilter: (sender: string) => void
	// 送信者フィルタを「全員選択」に戻す
	onSelectAllSenders: () => void
	// 送信者フィルタの選択を全部外す (0 件表示。ここから 1 人ずつ足していける)
	onClearSenderFilter: () => void
	onToggleSessionFilter: (kind: "busy" | "idle") => void
	onChangeRcFilter: (value: RcFilterValue) => void
	onChangeSearchKeyword: (keyword: SearchFilterValue) => void
	onResetFilters: () => void
	onFocusMention: (id: string) => void
	onComplete: (id: string) => void
	onCompleteAll: (id: string) => void
	onSetTomorrow: (id: string) => void
	onSetDueDate: (id: string) => void
	onPriorityUp: (id: string) => void
	onPriorityDown: (id: string) => void
	onStartEditComment: (id: string) => void
	onSaveComment: (id: string, comment: string) => void
	onCancelEditComment: () => void
	onChangeEditingComment: (comment: string) => void
	onOpenSlack: (id: string) => void
	// Slack を開くときにブラウザではなく Slack アプリ(deep link)で開くかの設定
	slackAppSetting: SlackAppSetting
	onChangeSlackAppEnabled: (enabled: boolean) => void
	onSyncMentions: () => void
	onCreateMention: () => void
	// Seq3: node ツリー関連
	onToggleChildNodesCollapsed: (
		mentionId: string,
		currentIsCollapsed?: boolean
	) => void
	// node 追加はセッション作成モーダル (node 新規作成モード) を開く。
	onAddRootNode: (mentionId: string) => void
	onHandOverEmbedSession: (mentionId: string) => void
	onUpdateNode: (id: string, name: string, isCollapsed?: boolean) => void
	onArchiveNode: (id: string) => void
	onAddChildNode: (milestoneId: string | null, parentNodeId: string) => void
	// Seq3: node ツリー操作ハンドラ
	onNodePriorityUp: (nodeId: string) => void
	onNodePriorityDown: (nodeId: string) => void
	onNodeCreateSession: (nodeId: string, nodeName: string) => void
	onNodeCompleteSession: (
		nodeId: string,
		nodeName: string,
		hasSession: boolean
	) => void
	onNodePendSession: (nodeId: string) => void
	onNodeOpenSlack: (nodeId: string, threadTs: string, channelId: string) => void
	onEmbeddedSessionOpenSlack: (channelId: string, threadTs: string) => void
	// embed session の Fork: フォーク先ノードは mentionId の mention 直下の nodes (root node) に作る
	onEmbeddedSessionFork?: (
		mentionId: string,
		nodeId: string,
		nodeName: string
	) => void
	onNodeCopyBranch: (nodeId: string) => void
	onNodeForkSession?: (nodeId: string, nodeName: string) => void
	onNodeOpenMemo: (node: SharedNode) => void
	onNodeMove: (
		nodeId: string,
		targetParentNodeId: string | null,
		afterNodeId: string | null
	) => void
	onNodeReorder: (parentNodeId: string | null, nodeIds: string[]) => void
	// Focus navigation for cross mention/tree keyboard nav
	onNodeFocusEscapeUp?: (mentionId: string) => void
	onNodeFocusEscapeDown?: (mentionId: string) => void
	nodeTreeFocusRequest?: NodeTreeFocusRequest
	// あるメンションのツリーで node がフォーカスされたか（child → parent）。
	onNodeFocusChange?: (mentionId: string, nodeId: string | null) => void
}

const SlackMentionsSection: FC<SlackMentionsSectionProps> = ({
	mentions: maskedMentions,
	polledSessionByNodeId,
	syncState: maskedSyncState,
	loading,
	error,
	syncStateLoading,
	isSyncing,
	isReadOnly,
	focusedMentionId,
	editingMentionId,
	editingComment,
	overrides,
	priorityFilter,
	dueDateFilter,
	senderFilter,
	sessionFilter,
	searchKeyword,
	senderOptions,
	priorityFilterCounts,
	dueDateFilterCounts,
	senderFilterCounts,
	sessionFilterCounts,
	onTogglePriorityFilter,
	onToggleDueDateFilter,
	onToggleSenderFilter,
	onSelectAllSenders,
	onClearSenderFilter,
	onToggleSessionFilter,
	onChangeRcFilter,
	onChangeSearchKeyword,
	onResetFilters,
	onFocusMention,
	onComplete,
	onCompleteAll,
	onSetTomorrow,
	onSetDueDate,
	onPriorityUp,
	onPriorityDown,
	onStartEditComment,
	onSaveComment,
	onCancelEditComment,
	onChangeEditingComment,
	onOpenSlack,
	slackAppSetting,
	onChangeSlackAppEnabled,
	onSyncMentions,
	onCreateMention,
	onToggleChildNodesCollapsed,
	onAddRootNode,
	onHandOverEmbedSession,
	onUpdateNode,
	onArchiveNode,
	onAddChildNode,
	onNodePriorityUp,
	onNodePriorityDown,
	onNodeCreateSession,
	onNodeCompleteSession,
	onNodePendSession,
	onNodeOpenSlack,
	onEmbeddedSessionOpenSlack,
	onEmbeddedSessionFork,
	onNodeCopyBranch,
	onNodeForkSession,
	onNodeOpenMemo,
	onNodeMove,
	onNodeReorder,
	onNodeFocusEscapeUp,
	onNodeFocusEscapeDown,
	nodeTreeFocusRequest,
	onNodeFocusChange,
}) => {
	// セッションの複数選択 (チェックボックス)。選択キーは cc-server の session id。
	// 選択中は画面上部に固定バーを出し、選択セッションを一括で開くコマンドをコピーできる。
	const [selectedCcSessions, setSelectedCcSessions] = useState<
		ReadonlyMap<string, SelectedCcSession>
	>(new Map())
	const [copiedMode, setCopiedMode] = useState<"editor" | "panel" | null>(null)
	// コピー先の Cursor workspace を refund-hub1 ~ 7 のどれで開いているかを覚えておく。
	// 1 なら cc.sh をそのまま、それ以外なら ../refund-hub1/... 経由 + --repo-root . を付ける。
	const [worktreeSlot, setWorktreeSlotState] =
		useState<WorktreeSlot>(loadWorktreeSlot)
	const handleChangeWorktreeSlot = useCallback((slot: WorktreeSlot) => {
		setWorktreeSlotState(slot)
		saveWorktreeSlot(slot)
	}, [])
	const copyResetTimerRef = useRef<number | null>(null)
	const toggleCcSession = useCallback((session: SelectedCcSession) => {
		setSelectedCcSessions((prev) => {
			const next = new Map(prev)
			if (next.has(session.ccSessionId)) {
				next.delete(session.ccSessionId)
			} else {
				next.set(session.ccSessionId, session)
			}
			return next
		})
	}, [])
	// コピーしたコマンドは Cursor (デスクトップ) で実行する前提のため、スマホでは選択機能ごと無効にする
	const isMobile = useIsMobile()
	const selection = useMemo(
		() => (isMobile ? null : { selectedCcSessions, toggleCcSession }),
		[isMobile, selectedCcSessions, toggleCcSession]
	)
	const clearSelection = useCallback(() => {
		setSelectedCcSessions(new Map())
		setCopiedMode(null)
	}, [])
	// mode は cc.sh の第1引数 (editor: エディタ領域にタブで開く / panel: ターミナルパネル分割で開く)
	const handleCopySelection = useCallback(
		(mode: "editor" | "panel") => {
			const command = buildCcCommand(
				mode,
				[...selectedCcSessions.values()],
				worktreeSlot
			)
			copyTextToClipboard(command).then(() => {
				setCopiedMode(mode)
				if (copyResetTimerRef.current !== null) {
					window.clearTimeout(copyResetTimerRef.current)
				}
				copyResetTimerRef.current = window.setTimeout(
					() => setCopiedMode(null),
					1500
				)
			})
		},
		[selectedCcSessions, worktreeSlot]
	)

	if (loading) {
		return <div className={styles.loading}>読み込み中...</div>
	}

	if (error) {
		return (
			<div className={styles.loading}>
				エラーが発生しました: {error.message}
			</div>
		)
	}

	const mentions = maskedMentions.map((mention) =>
		getFragmentData(SlackMentionsSectionMentionFragment, mention)
	)
	const syncState = getFragmentData(
		SlackMentionsSectionSyncStateFragment,
		maskedSyncState
	)
	const hasActiveFilter =
		priorityFilter.length > 0 ||
		dueDateFilter.length > 0 ||
		// 送信者は null (全員選択) がデフォルト。配列になっていれば絞り込み中。
		senderFilter !== null ||
		searchKeyword !== "" ||
		// セッションフィルタは busy/idle OFF + RC「すべて」がデフォルト(=非フィルタ)。
		sessionFilter.busy ||
		sessionFilter.idle ||
		sessionFilter.rc !== "all"

	return (
		<div className={styles.tabPanel}>
			{selection !== null && selectedCcSessions.size > 0 && (
				<div className={styles.sessionSelectionBar}>
					<span className={styles.sessionSelectionCount}>
						{selectedCcSessions.size} 件選択中
					</span>
					<div className={styles.sessionSelectionActions}>
						<label className={styles.sessionSelectionSlotLabel}>
							refund-hub
							<select
								className={styles.sessionSelectionSlotSelect}
								value={worktreeSlot}
								onChange={(e) =>
									handleChangeWorktreeSlot(e.target.value as WorktreeSlot)
								}
								title="Cursor で開いている workspace の refund-hubN を選ぶ。1 以外を選ぶと ../refund-hub1/... 経由 + --repo-root . でコマンドが生成される"
							>
								{WORKTREE_SLOTS.map((slot) => (
									<option key={slot} value={slot}>
										{slot}
									</option>
								))}
							</select>
						</label>
						<button
							type="button"
							className={styles.sessionSelectionCopyButton}
							onClick={() => handleCopySelection("editor")}
						>
							{copiedMode === "editor"
								? "✓ コピーしました"
								: "エディタ用をコピー"}
						</button>
						<button
							type="button"
							className={styles.sessionSelectionCopyButton}
							onClick={() => handleCopySelection("panel")}
						>
							{copiedMode === "panel"
								? "✓ コピーしました"
								: "ターミナル用をコピー"}
						</button>
						<button
							type="button"
							className={styles.sessionSelectionCancelButton}
							onClick={clearSelection}
						>
							キャンセル
						</button>
					</div>
				</div>
			)}
			<div className={styles.mentionsHeader}>
				<span className={styles.mentionCount}>未対応 {mentions.length} 件</span>
				<div className={styles.syncControls}>
					{!isReadOnly && (
						<div className={styles.syncAndCreateControls}>
							<button
								type="button"
								className={styles.syncButton}
								onClick={onCreateMention}
							>
								手動作成
							</button>
							<button
								type="button"
								className={styles.syncButton}
								onClick={onSyncMentions}
								disabled={isSyncing}
							>
								{isSyncing ? "同期中..." : "今すぐ同期"}
							</button>
						</div>
					)}
					<label className={styles.slackWindowControl}>
						<input
							type="checkbox"
							checked={slackAppSetting.enabled}
							onChange={(e) => onChangeSlackAppEnabled(e.target.checked)}
						/>
						Slackアプリで開く
					</label>
					<span className={styles.syncState}>
						最終同期:{" "}
						{syncStateLoading
							? "読み込み中"
							: formatSyncRelativeTime(syncState?.lastSyncTime)}
					</span>
				</div>
			</div>

			<div className={styles.mentionsSearchBar}>
				<span className={styles.filterLabel}>検索</span>
				<input
					type="search"
					className={styles.searchInput}
					value={searchKeyword}
					placeholder="メモを部分一致で検索 (スペース区切りで AND)"
					onChange={(event) => onChangeSearchKeyword(event.target.value)}
					onKeyDown={(event) => {
						// Escape でキーワードを消してフォーカスを外す (一覧のキーボード操作に戻る)
						if (event.key !== "Escape") return
						event.preventDefault()
						onChangeSearchKeyword("")
						event.currentTarget.blur()
					}}
				/>
				<span className={styles.searchHint}>
					mention のメモ / node のメモを検索
				</span>
			</div>

			<div className={styles.mentionsFilterBar}>
				<div className={styles.filterGroup}>
					<span className={styles.filterLabel}>優先度</span>
					<div className={styles.filterOptions}>
						{PRIORITY_FILTER_OPTIONS.map((option) => {
							const count = priorityFilterCounts[String(option.value)] ?? 0
							// null (全部) は「選択なし」のとき active。それ以外は選択配列に含まれていれば active。
							const isOptionActive =
								option.value === null
									? priorityFilter.length === 0
									: priorityFilter.includes(option.value)
							return (
								<button
									key={String(option.value)}
									type="button"
									className={
										isOptionActive
											? styles.filterButtonActive
											: styles.filterButton
									}
									onClick={() => onTogglePriorityFilter(option.value)}
								>
									{option.label} ({count})
								</button>
							)
						})}
					</div>
				</div>
				<div className={styles.filterGroup}>
					<span className={styles.filterLabel}>セッション</span>
					<div className={styles.filterOptions}>
						{/* busy = BUSY 状態のセッションノードを持つメンション。
						    idle = BUSY 以外(IDLE / PENDING)のセッションノードを持つメンション。
						    デフォルトは両方 OFF(全件表示)。どちらか/両方を押すとセッションノードを
						    持つメンションだけに絞り込む(押した状態のバケットを持つものを表示)。 */}
						<button
							type="button"
							className={
								sessionFilter.busy
									? styles.filterButtonActive
									: styles.filterButton
							}
							onClick={() => onToggleSessionFilter("busy")}
						>
							busy ({sessionFilterCounts.busy})
						</button>
						<button
							type="button"
							className={
								sessionFilter.idle
									? styles.filterButtonActive
									: styles.filterButton
							}
							onClick={() => onToggleSessionFilter("idle")}
						>
							idle ({sessionFilterCounts.idle})
						</button>
					</div>
				</div>
				<div className={styles.filterGroup}>
					<span className={styles.filterLabel}>RC</span>
					<div className={styles.filterOptions}>
						{/* RC = Remote Control (Claude アプリ) 有効セッションの3値フィルタ。
						    すべて (デフォルト) / RC (RC 有効セッションを持つ mention のみ) /
						    RC外 (RC 有効セッションを持つ mention を除外)。排他選択で、
						    busy/idle の絞り込みとは独立に効く。 */}
						{RC_FILTER_OPTIONS.map((option) => {
							const count =
								option.value === "all"
									? sessionFilterCounts.all
									: option.value === "rc"
										? sessionFilterCounts.rc
										: sessionFilterCounts.nonRc
							return (
								<button
									key={option.value}
									type="button"
									className={
										sessionFilter.rc === option.value
											? styles.filterButtonActive
											: styles.filterButton
									}
									onClick={() => onChangeRcFilter(option.value)}
									title={
										option.value === "all"
											? "RC の有無で絞り込まない"
											: option.value === "rc"
												? "Remote Control (Claude アプリからの操作) 有効セッションを持つメンションだけ表示する"
												: "Remote Control (Claude アプリからの操作) 有効セッションを持つメンションを除外する"
									}
								>
									{option.label} ({count})
								</button>
							)
						})}
					</div>
				</div>
				<div className={styles.filterGroup}>
					<span className={styles.filterLabel}>期限</span>
					<div className={styles.filterOptions}>
						{DUE_DATE_FILTER_OPTIONS.map((option) => {
							const count = dueDateFilterCounts[String(option.value)] ?? 0
							// null (全て) は「選択なし」のとき active。それ以外は選択配列に含まれていれば active。
							const isOptionActive =
								option.value === null
									? dueDateFilter.length === 0
									: dueDateFilter.includes(option.value)
							return (
								<button
									key={String(option.value)}
									type="button"
									className={
										isOptionActive
											? styles.filterButtonActive
											: styles.filterButton
									}
									onClick={() => onToggleDueDateFilter(option.value)}
								>
									{option.label} ({count})
								</button>
							)
						})}
					</div>
				</div>
				<div className={styles.filterGroup}>
					<span className={styles.filterLabel}>送信者</span>
					<div className={styles.filterOptions}>
						{/* 送信者は複数選択 (OR)。デフォルト (senderFilter === null) は全員選択で、
						    各送信者ボタンが active 表示になる。「すべて」で全員選択に戻し、
						    「クリア」で全員の選択を外す (0 件表示。そこから 1 人ずつ足していける)。 */}
						<button
							type="button"
							className={
								senderFilter === null
									? styles.filterButtonActive
									: styles.filterButton
							}
							onClick={onSelectAllSenders}
							title="全送信者を選択する (フィルタ解除)"
						>
							すべて
						</button>
						<button
							type="button"
							className={styles.filterButton}
							onClick={onClearSenderFilter}
							title="送信者の選択を全部外す"
						>
							クリア
						</button>
						{senderOptions.map((sender) => {
							// null (全員選択) のときは全ボタンを active 表示にする
							const isOptionActive =
								senderFilter === null || senderFilter.includes(sender)
							return (
								<button
									key={sender}
									type="button"
									className={
										isOptionActive
											? styles.filterButtonActive
											: styles.filterButton
									}
									onClick={() => onToggleSenderFilter(sender)}
								>
									@{sender} ({senderFilterCounts[sender] ?? 0})
								</button>
							)
						})}
					</div>
				</div>
				<button
					type="button"
					className={styles.resetFilterButton}
					disabled={!hasActiveFilter}
					onClick={onResetFilters}
				>
					すべて解除
				</button>
			</div>

			{mentions.length === 0 ? (
				<div className={styles.mentionsEmpty}>
					未対応のメンションはありません
				</div>
			) : (
				<div className={styles.mentionList}>
					<SessionSelectionContext.Provider value={selection}>
						{mentions.map((mention) => (
							<MentionRow
								key={mention.id}
								mention={mention}
								polledSessionByNodeId={polledSessionByNodeId}
								override={overrides[mention.id]}
								isFocused={mention.id === focusedMentionId}
								isEditingComment={mention.id === editingMentionId}
								editingComment={editingComment}
								isReadOnly={isReadOnly}
								onFocus={() => onFocusMention(mention.id)}
								onOpenSlack={() => onOpenSlack(mention.id)}
								onSetTomorrow={() => onSetTomorrow(mention.id)}
								onSetDueDate={() => onSetDueDate(mention.id)}
								onPriorityUp={() => onPriorityUp(mention.id)}
								onPriorityDown={() => onPriorityDown(mention.id)}
								onStartEditComment={() => onStartEditComment(mention.id)}
								onComplete={() => onComplete(mention.id)}
								onCompleteAll={() => onCompleteAll(mention.id)}
								onChangeEditingComment={onChangeEditingComment}
								onSaveComment={() =>
									onSaveComment(mention.id, normalizeComment(editingComment))
								}
								onCancelEditComment={onCancelEditComment}
								onToggleChildNodesCollapsed={(currentIsCollapsed) =>
									onToggleChildNodesCollapsed(mention.id, currentIsCollapsed)
								}
								onAddRootNode={() => onAddRootNode(mention.id)}
								onHandOverEmbedSession={() =>
									onHandOverEmbedSession(mention.id)
								}
								onUpdateNode={onUpdateNode}
								onArchiveNode={onArchiveNode}
								onAddChildNode={onAddChildNode}
								onNodePriorityUp={onNodePriorityUp}
								onNodePriorityDown={onNodePriorityDown}
								onNodeCreateSession={onNodeCreateSession}
								onNodeCompleteSession={onNodeCompleteSession}
								onNodePendSession={onNodePendSession}
								onNodeOpenSlack={onNodeOpenSlack}
								onEmbeddedSessionOpenSlack={onEmbeddedSessionOpenSlack}
								onEmbeddedSessionFork={
									onEmbeddedSessionFork
										? (nodeId, nodeName) =>
												onEmbeddedSessionFork(mention.id, nodeId, nodeName)
										: undefined
								}
								onNodeCopyBranch={onNodeCopyBranch}
								onNodeForkSession={onNodeForkSession}
								onNodeOpenMemo={onNodeOpenMemo}
								onNodeMove={onNodeMove}
								onNodeReorder={onNodeReorder}
								onNodeFocusEscapeUp={
									onNodeFocusEscapeUp
										? () => onNodeFocusEscapeUp(mention.id)
										: undefined
								}
								onNodeFocusEscapeDown={
									onNodeFocusEscapeDown
										? () => onNodeFocusEscapeDown(mention.id)
										: undefined
								}
								nodeTreeFocusRequest={nodeTreeFocusRequest}
								onNodeFocusChange={
									onNodeFocusChange
										? (nodeId) => onNodeFocusChange(mention.id, nodeId)
										: undefined
								}
							/>
						))}
					</SessionSelectionContext.Provider>
				</div>
			)}
		</div>
	)
}

export default SlackMentionsSection
